import { SUPABASE_PUBLISHABLE_KEY } from '../../lib/supabase';
import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { fetchWithTimeout, bookSearchHeaders } from '../../lib/bookSearch';
import { useAuth } from '../../hooks/useAuth';
import { getCurrentUser } from '../../lib/auth';
import { getPurchaseLinks } from '../../hooks/useLibrary';
import { notifyBookAdded } from './LibraryDock';
import { bookGenreNames } from '../../lib/genreTaxonomy';
import { X } from 'lucide-react';

import { onCoverError } from '../../lib/coverFallback';

// Enrichment, not a search box. Neither call below blocks the modal: the
// first fills in the rating after open, the second runs on an explicit click
// with its own spinner. The reader is looking at a usable modal the whole
// time, so the 4s search-box default is needlessly tight — it throws away a
// slow-but-good answer for no gain. Same value and name as the equivalent
// call in Add5StarReadPopup.jsx.
const ENRICH_TIMEOUT_MS = 10000;

function useIsMobile() {
  const [isMobile, setIsMobile] = React.useState(
    typeof window !== 'undefined' ? window.innerWidth < 768 : false
  );
  React.useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  return isMobile;
}

// ── Goodreads enrichment helpers ──────────────────────────────────────────
// Basic enrichment (rating, genres, isbn, pages) — safe to call automatically
// on every modal open. Cache-first: if the book already has a goodreads_id,
// this skips straight to the fast /books/{id} lookup, no search needed.
async function fetchGoodreadsData(title, author, bookId) {
  if (!title) return null;
  try {
    const { data: { session } } = await supabase.auth.getSession();
    const res = await fetchWithTimeout(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/goodreads-enrich`, {
      method: 'POST',
      headers: bookSearchHeaders(session, SUPABASE_PUBLISHABLE_KEY),
      body: JSON.stringify({ title, author, bookId: bookId || undefined }),
    }, ENRICH_TIMEOUT_MS);
    if (!res.ok) return null;
    return await res.json();
  } catch (err) {
    console.warn('[goodreads-enrich] failed:', err.message);
    return null;
  }
}

// Reviews / similar books — deliberately NOT called on modal open. Only
// fired when the reader clicks to expand that section.
async function fetchGoodreadsExtras(goodreadsId, type) {
  if (!goodreadsId) return null;
  try {
    const { data: { session } } = await supabase.auth.getSession();
    const res = await fetchWithTimeout(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/goodreads-extras`, {
      method: 'POST',
      headers: bookSearchHeaders(session, SUPABASE_PUBLISHABLE_KEY),
      body: JSON.stringify({ goodreadsId, type }),
    }, ENRICH_TIMEOUT_MS);
    if (!res.ok) return null;
    return await res.json();
  } catch (err) {
    console.warn('[goodreads-extras] failed:', err.message);
    return null;
  }
}

// ── Goodreads reviews + similar books — shared between mobile & desktop ───
function GoodreadsExtrasSection({ goodreadsId, isMobile }) {
  const [reviews,       setReviews]       = useState(null);
  const [reviewsLoading,setReviewsLoading]= useState(false);
  const [similar,       setSimilar]       = useState(null);
  const [similarLoading,setSimilarLoading]= useState(false);

  if (!goodreadsId) return null;

  async function loadReviews() {
    if (reviews || reviewsLoading) return;
    setReviewsLoading(true);
    const data = await fetchGoodreadsExtras(goodreadsId, 'reviews');
    setReviews(data?.reviews || []);
    setReviewsLoading(false);
  }

  async function loadSimilar() {
    if (similar || similarLoading) return;
    setSimilarLoading(true);
    const data = await fetchGoodreadsExtras(goodreadsId, 'similar');
    setSimilar(data?.similar || []);
    setSimilarLoading(false);
  }

  const btnStyle = {
    flex: 1, padding: isMobile ? '10px' : '11px', background: 'var(--brand-paper)', border: '1px solid var(--brand-sand)',
    borderRadius: 'var(--radius-10)', fontSize: isMobile ? 12 : 13, fontWeight: 600, color: 'var(--neutral-600)', cursor: 'pointer',
  };

  return (
    <div style={{ marginBottom: 'var(--space-lg)', paddingTop: 'var(--space-xs)' }}>
      <p style={{ fontSize: 'var(--font-size-xxs)', fontWeight: 700, color: 'var(--neutral-400)', margin: '0 0 8px', letterSpacing: 0.5, textTransform: 'uppercase' }}>From Goodreads</p>
      <div style={{ display: 'flex', gap: 'var(--space-sm)', marginBottom: 'var(--space-10)' }}>
        <button onClick={loadReviews} style={btnStyle}>
          {reviewsLoading ? 'Loading…' : reviews ? `${reviews.length} Reviews` : 'Show Reviews'}
        </button>
        <button onClick={loadSimilar} style={btnStyle}>
          {similarLoading ? 'Loading…' : similar ? 'Similar Books' : 'Readers Also Enjoyed'}
        </button>
      </div>

      {reviews && reviews.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-sm)', marginBottom: 'var(--space-10)' }}>
          {reviews.slice(0, 3).map((r, i) => (
            <div key={i} style={{ padding: '10px 12px', background: 'var(--brand-paper)', borderRadius: 'var(--radius-10)', border: '1px solid var(--neutral-100)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 'var(--space-xs)' }}>
                <span style={{ fontSize: 'var(--font-size-sm)', fontWeight: 600, color: 'var(--neutral-800)' }}>{r.reviewer}</span>
                {r.rating && <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--feedback-warning)' }}>{'★'.repeat(Math.round(r.rating))}</span>}
              </div>
              <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--neutral-600)', lineHeight: 1.5, margin: 'var(--space-none)', overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical' }}>{r.body}</p>
            </div>
          ))}
        </div>
      )}
      {reviews && reviews.length === 0 && (
        <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--neutral-400)', fontStyle: 'italic', margin: '0 0 10px' }}>No reviews available yet.</p>
      )}

      {similar && similar.length > 0 && (
        <div style={{ display: 'flex', gap: 'var(--space-sm)', overflowX: 'auto', paddingBottom: 'var(--space-xs)' }}>
          {similar.slice(0, 8).map((b, i) => (
            <a key={i} href={b.goodreadsUrl} target="_blank" rel="noopener noreferrer" style={{ flexShrink: 0, width: 64, textDecoration: 'none' }}>
              <div style={{ width: 64, height: 92, borderRadius: 'var(--radius-sm)', overflow: 'hidden', background: 'var(--neutral-100)', marginBottom: 'var(--space-xs)' }}>
                {b.coverImage
                  ? <img src={b.coverImage} alt={b.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={onCoverError} />
                  : <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 'var(--font-size-lg)' }}>📚</div>}
              </div>
              <p style={{ fontSize: 'var(--font-size-xxs)', color: 'var(--neutral-600)', margin: 'var(--space-none)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{b.title}</p>
            </a>
          ))}
        </div>
      )}
      {similar && similar.length === 0 && (
        <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--neutral-400)', fontStyle: 'italic', margin: 'var(--space-none)' }}>No similar books found.</p>
      )}
    </div>
  );
}

export function BookDetailModal({ bookId, onClose, onAuthRequired }) {
  const { user } = useAuth();
  const isMobile = useIsMobile();

  const [book, setBook] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const [adding, setAdding] = useState(false);
  const [purchaseLinks, setPurchaseLinks] = useState([]);
  const [isInLibrary, setIsInLibrary] = useState(false);
  const [currentShelf, setCurrentShelf] = useState(null);
  // Goodreads basic enrichment — resolved automatically once the book loads.
  // Cache-first: if book.goodreads_id is already set, this is a single fast
  // call; otherwise it resolves + caches it back onto the books row so this
  // never has to search again for this book, from either portal.
  const [goodreadsData,     setGoodreadsData]     = useState(null);
  const [goodreadsChecking, setGoodreadsChecking] = useState(false);

  useEffect(() => {
    if (bookId) { fetchBookDetails(); fetchPurchaseLinks(); }
  }, [bookId]);

  useEffect(() => {
    if (bookId && user) checkLibrary();
  }, [bookId, user]);

  async function fetchBookDetails() {
    setLoading(true);
    const { data, error } = await supabase
      .from('books')
      .select(`
        id, title, author_name, cover_image_url, description,
        avg_rating, page_count, genres, goodreads_id,
        book_tags ( tag_type, source, tropes ( canonical_name, category ) ),
        book_genres ( id, source, genre:genres ( name ) ),
        book_subgenres ( id, source, subgenre:subgenres ( name ) )
      `)
      .eq('id', bookId)
      .single();

    if (!error && data) {
      const grouped = {};
      let hasAuthorTags = false;
      (data.book_tags || []).forEach(bt => {
        const type = bt.tag_type;
        const name = bt.tropes?.canonical_name;
        if (!type || !name) return;
        if (!grouped[type]) grouped[type] = [];
        grouped[type].push(name);
        if (bt.source === 'author') hasAuthorTags = true;
      });
      // An author's genre used to arrive as a book_tags 'genre' row and count
      // here. Since PR 5 it lives only in book_genres / book_subgenres, so
      // look there too, or an author who set just a genre stops reading as
      // author-verified.
      if ([...(data.book_genres || []), ...(data.book_subgenres || [])].some(r => r?.source === 'author')) {
        hasAuthorTags = true;
      }
      const nextBook = {
        authorVerified: hasAuthorTags,
        id: data.id, title: data.title, author: data.author_name,
        coverUrl: data.cover_image_url, description: data.description,
        avgRating: data.avg_rating, pageCount: data.page_count,
        goodreadsId: data.goodreads_id || null,
        // From book_genres / book_subgenres: since PR 5 nothing writes a
        // book_tags genre row. grouped['genre'] is only the fallback for a
        // book with no row in either new table. See bookGenreNames.
        tropes: grouped['trope'] || [], genre: bookGenreNames(data, grouped['genre']),
        moods: grouped['tone'] || [], spice: grouped['spice'] || [],
        triggers: grouped['trigger'] || [], pov: grouped['pov'] || [],
        settings: grouped['setting'] || [], representation: grouped['representation'] || [],
      };
      setBook(nextBook);

      // Kick off Goodreads enrichment in the background. Cheap when goodreads_id
      // is already cached (single detail call); resolves + caches it the first
      // time otherwise.
      setGoodreadsChecking(true);
     fetchGoodreadsData(nextBook.title, nextBook.author, nextBook.id)
        .then(setGoodreadsData)
        .catch(err => console.error('Goodreads enrichment failed:', err.message))
        .finally(() => setGoodreadsChecking(false));
    }
    setLoading(false);
  }

  async function fetchPurchaseLinks() {
    const links = await getPurchaseLinks(bookId);
    setPurchaseLinks(links);
  }

  async function checkLibrary() {
    const { data } = await supabase
      .from('user_library').select('id, shelf')
      .eq('user_id', user.id).eq('book_id', bookId).maybeSingle();
    setIsInLibrary(!!data);
    setCurrentShelf(data?.shelf || null);
  }

  // A signed-out reader gets the page's own sign-up pop-up through
  // onAuthRequired (the homepage deck passes gate.requireAuth), and the save
  // carries on once they sign in. `user` is the value from this render, so the
  // retry reads getCurrentUser() instead -- same reason as MatchMeCard's
  // handleSave. Without the prop (nothing passes one today but the deck), the
  // old alert stays rather than doing nothing.
  async function handleAddToLibrary(shelf = 'tbr') {
    const forUser = user || await getCurrentUser();
    if (!forUser) {
      if (onAuthRequired) onAuthRequired(() => handleAddToLibrary(shelf));
      else alert('Please log in to add books to your library');
      return;
    }
    if (adding) return;
    setAdding(true);
    const { error } = await supabase.from('user_library').upsert({
      user_id: forUser.id, book_id: bookId, shelf, date_added: new Date().toISOString(),
    }, { onConflict: 'user_id,book_id' });
    if (error) {
      console.error('Failed to add book:', error);
      alert('Could not save that book. Please try again.');
    } else {
      setIsInLibrary(true);
      setCurrentShelf(shelf);
      notifyBookAdded({ id: book.id, title: book.title, author: book.author, coverUrl: book.coverUrl, color: '#9E4B2C', shelf });
      onClose();
    }
    setAdding(false);
  }

  const SHELF_LABELS = {
    tbr: '📚 To Read', reading: '📖 Reading', read: '✅ Read', loved: '❤️ Loved',
  };

  const spiceLevel = book?.spice?.length > 0 ? book.spice[0] : null;
  const SPICE_ORDER = ['No Spice', 'Closed Door', 'Mild', 'Moderate', 'Spicy', 'Scorching', 'Steamy', 'Explicit'];
  const spiceNum = spiceLevel
    ? Math.max(1, SPICE_ORDER.findIndex(s => spiceLevel.toLowerCase().includes(s.toLowerCase().split(' ')[0])))
    : 0;

  const SpiceIndicator = ({ level }) => (
    <div style={{ display: 'flex', gap: 'var(--space-xxs)' }}>
      {[1,2,3,4,5].map(i => <span key={i} style={{ fontSize: 'var(--font-size-base)', opacity: i <= level ? 1 : 0.2 }}>🌶️</span>)}
    </div>
  );

  const TagRow = ({ label, items, bg, color }) => items?.length > 0 ? (
    <div style={{ marginBottom: 'var(--space-md)' }}>
      <p style={{ fontSize: 'var(--font-size-xxs)', fontWeight: 700, color: 'var(--neutral-400)', margin: '0 0 5px', letterSpacing: 0.5, textTransform: 'uppercase' }}>{label}</p>
      <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
        {items.map((t, i) => (
          <span key={i} style={{ fontSize: isMobile ? 11 : 12, padding: isMobile ? '3px 10px' : '4px 12px', borderRadius: 14, background: bg, color, fontWeight: 500 }}>{t}</span>
        ))}
      </div>
    </div>
  ) : null;

  const genre = book?.genre?.[0] || null;

  // Resolved Goodreads ID for reviews/similar lookups — prefer freshly-fetched
  // over the cached column, since the enrichment call may just have resolved
  // it for the first time this session.
  const resolvedGoodreadsId = goodreadsData?.goodreadsId || book?.goodreadsId || null;

  if (!bookId) return null;

  // ── MOBILE: bottom sheet ──────────────────────────────────────────────────
  if (isMobile) {
    return (
      <div
        style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 500, display: 'flex', alignItems: 'flex-end' }}
        onClick={e => { if (e.target === e.currentTarget) onClose(); }}
      >
        <div
          style={{ width: '100%', background: 'var(--brand-white)', borderRadius: '20px 20px 0 0', maxHeight: '92vh', display: 'flex', flexDirection: 'column', animation: 'sheetUp 0.25s ease-out', fontFamily: 'var(--font-ui)' }}
          onClick={e => e.stopPropagation()}
        >
          {/* Drag handle */}
          <div style={{ display: 'flex', justifyContent: 'center', padding: '10px 0 4px' }}>
            <div style={{ width: 36, height: 4, background: 'var(--neutral-300)', borderRadius: 2 }} />
          </div>

          {/* Close button */}
          <button onClick={onClose} style={{ position: 'absolute', top: 12, right: 14, background: 'var(--neutral-100)', border: 'none', width: 28, height: 28, borderRadius: 'var(--radius-circle)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', zIndex: 10 }}>
            <X size={14} color="#78716c" />
          </button>

          {/* Scrollable body */}
          <div style={{ flex: 1, overflowY: 'auto', scrollbarWidth: 'none' }}>

            {loading ? (
              <div style={{ padding: '20px 16px' }}>
                <div style={{ height: 180, background: 'var(--neutral-100)', borderRadius: 'var(--radius-lg)', marginBottom: 'var(--space-md)' }} />
                <div style={{ height: 20, background: 'var(--neutral-100)', borderRadius: 6, marginBottom: 'var(--space-sm)' }} />
                <div style={{ height: 14, background: 'var(--neutral-100)', borderRadius: 6, width: '60%' }} />
              </div>
            ) : book ? (
              <>
                {/* Hero: cover + title side by side */}
                <div style={{ display: 'flex', gap: 'var(--space-14)', padding: '8px 16px 14px', borderBottom: '1px solid var(--neutral-100)' }}>
                  <div style={{ width: 90, height: 130, borderRadius: '3px 8px 8px 3px', overflow: 'hidden', flexShrink: 0, background: 'linear-gradient(135deg,var(--feedback-warning-bg),var(--brand-cream))', boxShadow: '2px 2px 10px rgba(0,0,0,0.15)' }}>
                    {book.coverUrl
                      ? <img src={book.coverUrl} alt={book.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={onCoverError} />
                      : <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 'var(--font-size-h2)' }}>📖</div>
                    }
                  </div>
                  <div style={{ flex: 1, minWidth: 0, paddingTop: 'var(--space-xs)' }}>
                    <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 17, fontWeight: 700, color: 'var(--neutral-800)', margin: '0 0 3px', lineHeight: 1.2 }}>{book.title}</h2>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-6)', marginBottom: 'var(--space-sm)', flexWrap: 'wrap' }}>
                      <p style={{ fontSize: 'var(--font-size-base)', color: 'var(--neutral-500)', margin: 'var(--space-none)' }}>by {book.author}</p>
                      {book.authorVerified && (
                        <span style={{ fontSize: 'var(--font-size-tiny)', fontWeight: 700, color: '#166534', background: '#dcfce7', border: '1px solid #bbf7d0', borderRadius: 6, padding: '1px 6px' }}>✓ Verified</span>
                      )}
                    </div>
                    {/* Stat pills */}
                    <div style={{ display: 'flex', gap: 'var(--space-6)', flexWrap: 'wrap' }}>
                      {book.avgRating && (
                        <span style={{ fontSize: 'var(--font-size-xs)', background: 'var(--feedback-warning-bg)', color: 'var(--feedback-warning-ink)', padding: '3px 8px', borderRadius: 'var(--radius-md)', fontWeight: 600 }}>⭐ {Number(book.avgRating).toFixed(1)}</span>
                      )}
                      {goodreadsData?.avgRating && (
                        <span style={{ fontSize: 'var(--font-size-xs)', background: 'var(--brand-cream)', color: '#6b4c3b', padding: '3px 8px', borderRadius: 'var(--radius-md)', fontWeight: 600 }}>
                          GR ★ {Number(goodreadsData.avgRating).toFixed(2)}{goodreadsData.ratingsCount ? ` (${goodreadsData.ratingsCount.toLocaleString()})` : ''}
                        </span>
                      )}
                      {book.pageCount && (
                        <span style={{ fontSize: 'var(--font-size-xs)', background: 'var(--neutral-100)', color: 'var(--neutral-500)', padding: '3px 8px', borderRadius: 'var(--radius-md)', fontWeight: 600 }}>{book.pageCount}p</span>
                      )}
                      {genre && (
                        <span style={{ fontSize: 'var(--font-size-xs)', background: 'var(--brand-cream)', color: '#6b4c3b', padding: '3px 8px', borderRadius: 'var(--radius-md)', fontWeight: 600 }}>{genre}</span>
                      )}
                    </div>
                    {spiceLevel && (
                      <div style={{ marginTop: 'var(--space-sm)' }}>
                        <SpiceIndicator level={spiceNum} />
                      </div>
                    )}
                  </div>
                </div>

                {/* Tags + content */}
                <div style={{ padding: '14px 16px' }}>

                  {/* Description */}
                  {book.description && (
                    <div style={{ marginBottom: 'var(--space-14)' }}>
                      <p style={{ fontSize: 'var(--font-size-xxs)', fontWeight: 700, color: 'var(--neutral-400)', margin: '0 0 5px', letterSpacing: 0.5, textTransform: 'uppercase' }}>Synopsis</p>
                      <p style={{ fontSize: 'var(--font-size-base)', color: 'var(--neutral-600)', lineHeight: 1.6, margin: 'var(--space-none)' }}>
                        {expanded ? book.description : book.description.slice(0, 160) + (book.description.length > 160 ? '…' : '')}
                      </p>
                      {book.description.length > 160 && (
                        <button onClick={() => setExpanded(!expanded)} style={{ background: 'none', border: 'none', color: 'var(--feedback-warning)', fontSize: 'var(--font-size-sm)', fontWeight: 600, cursor: 'pointer', padding: '4px 0', marginTop: 'var(--space-xxs)' }}>
                          {expanded ? 'Show less' : 'Read more'}
                        </button>
                      )}
                    </div>
                  )}

                  <TagRow label="Tropes" items={book.tropes} bg="#fef3c7" color="#92400e" />
                  <TagRow label="Mood" items={book.moods} bg="#292524" color="#faf8f5" />
                  <TagRow label="POV" items={book.pov} bg="#f5f0e8" color="#78716c" />
                  <TagRow label="Setting" items={book.settings} bg="#f0fdf4" color="#166534" />
                  <TagRow label="Representation" items={book.representation} bg="#f5f3ff" color="#7c3aed" />

                  {book.triggers.length > 0 && (
                    <div style={{ marginBottom: 'var(--space-md)' }}>
                      <p style={{ fontSize: 'var(--font-size-xxs)', color: 'var(--neutral-400)', margin: '0 0 4px' }}>⚠️ Content notes</p>
                      <div style={{ display: 'flex', gap: 'var(--space-xs)', flexWrap: 'wrap' }}>
                        {book.triggers.map((t, i) => (
                          <span key={i} style={{ fontSize: 'var(--font-size-xxs)', padding: '2px 8px', borderRadius: 6, background: 'var(--neutral-100)', color: 'var(--neutral-500)' }}>{t}</span>
                        ))}
                      </div>
                    </div>
                  )}

                  <GoodreadsExtrasSection goodreadsId={resolvedGoodreadsId} isMobile />

                  {/* Extra bottom padding so content doesn't hide behind footer */}
                  <div style={{ height: 8 }} />
                </div>
              </>
            ) : (
              <div style={{ textAlign: 'center', padding: '40px 0' }}>
                <p style={{ color: 'var(--neutral-500)' }}>Book not found</p>
              </div>
            )}
          </div>

          {/* Sticky footer */}
          {!loading && book && (
            <div style={{ padding: '12px 16px 20px', borderTop: '1px solid var(--neutral-100)', background: 'var(--brand-white)', display: 'flex', flexDirection: 'column', gap: 'var(--space-sm)', flexShrink: 0 }}>
              {isInLibrary ? (
                <div style={{ padding: '13px', background: 'var(--brand-forest)', color: 'var(--brand-white)', borderRadius: 'var(--radius-lg)', fontSize: 'var(--font-size-md)', fontWeight: 700, textAlign: 'center' }}>
                  {currentShelf ? `✓ ${SHELF_LABELS[currentShelf] || 'In Your Library'}` : '✓ Already in Your Library'}
                </div>
              ) : (
                <div style={{ display: 'flex', gap: 'var(--space-sm)' }}>
                  <button onClick={() => handleAddToLibrary('tbr')} disabled={adding}
                    style={{ flex: 2, padding: '13px', background: adding ? 'var(--neutral-600)' : 'var(--neutral-800)', color: 'var(--brand-white)', border: 'none', borderRadius: 'var(--radius-lg)', fontSize: 'var(--font-size-md)', fontWeight: 700, cursor: adding ? 'not-allowed' : 'pointer' }}>
                    {adding ? 'Adding...' : '📚 Add to Library'}
                  </button>
                  <button onClick={() => handleAddToLibrary('read')} disabled={adding}
                    style={{ flex: 1, padding: '13px', background: 'var(--neutral-100)', color: 'var(--neutral-800)', border: '1px solid var(--brand-sand)', borderRadius: 'var(--radius-lg)', fontSize: 'var(--font-size-sm)', fontWeight: 600, cursor: adding ? 'not-allowed' : 'pointer', whiteSpace: 'nowrap' }}>
                    ✅ Read
                  </button>
                </div>
              )}
              {/* Buy links */}
              <div style={{ display: 'flex', gap: 'var(--space-sm)' }}>
                {purchaseLinks.slice(0, 2).map((link, i) => (
                      <a key={i} href={link.url} target="_blank" rel="noopener noreferrer"
                        style={{ flex: 1, padding: '11px', background: 'var(--feedback-warning-bg)', color: 'var(--feedback-warning-ink)', border: '1px solid var(--feedback-warning-border)', borderRadius: 'var(--radius-lg)', fontSize: 'var(--font-size-base)', fontWeight: 600, textAlign: 'center', textDecoration: 'none' }}>
                        {link.retailer} ↗
                      </a>
                    ))}
              </div>
            </div>
          )}
        </div>

        <style>{`
          @keyframes sheetUp { from { transform: translateY(100%); } to { transform: translateY(0); } }
        `}</style>
      </div>
    );
  }

  // ── DESKTOP: centred modal (unchanged) ────────────────────────────────────
  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(6px)', zIndex: 500, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 'var(--space-lg)', fontFamily: 'var(--font-ui)' }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        style={{ position: 'relative', width: '90vw', maxWidth: 960, maxHeight: '90vh', background: 'var(--brand-white)', borderRadius: 'var(--radius-xxl)', overflow: 'hidden', boxShadow: '0 25px 60px rgba(0,0,0,0.3)', display: 'flex', flexDirection: 'column', animation: 'modalSlideUp 0.25s ease-out' }}
        onClick={e => e.stopPropagation()}
      >
        {/* Top bar */}
        <div style={{ padding: '12px 20px', borderBottom: '1px solid var(--neutral-100)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
          <span style={{ fontSize: 'var(--font-size-sm)', fontWeight: 700, color: 'var(--brand-rust)', background: 'var(--feedback-warning-bg)', padding: '4px 12px', borderRadius: 'var(--radius-10)', border: '1px solid var(--feedback-warning-border)' }}>📚 Book Details</span>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 'var(--font-size-xl)', color: 'var(--neutral-400)', cursor: 'pointer', width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 'var(--radius-circle)' }}>
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '24px 28px', scrollbarWidth: 'none' }}>
          {loading ? (
            <div style={{ display: 'flex', gap: 'var(--space-xxl)' }}>
              <div style={{ width: '100%', height: 380, background: 'var(--neutral-100)', borderRadius: 'var(--radius-lg)', flexShrink: 0 }} />
              <div style={{ flex: 1 }}>
                <div style={{ height: 32, background: 'var(--neutral-100)', borderRadius: 'var(--radius-md)', marginBottom: 'var(--space-md)' }} />
                <div style={{ height: 20, background: 'var(--neutral-100)', borderRadius: 'var(--radius-md)', width: '60%', marginBottom: 'var(--space-lg)' }} />
              </div>
            </div>
          ) : book ? (
            <div style={{ display: 'grid', gridTemplateColumns: '40% 60%', gap: 28 }}>

              {/* LEFT */}
              <div>
                <div style={{ width: '100%', height: 380, background: book.coverUrl ? 'none' : 'linear-gradient(135deg, #4a1a2e, #6b2a42)', borderRadius: '4px 12px 12px 4px', overflow: 'hidden', boxShadow: '4px 4px 20px rgba(0,0,0,0.2)', marginBottom: 'var(--space-14)', position: 'relative' }}>
                  {book.coverUrl
                    ? <img src={book.coverUrl} alt={book.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={onCoverError} />
                    : <><div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 6, background: 'rgba(0,0,0,0.3)' }} /><div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 'var(--font-size-hero)' }}>📖</div></>
                  }
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-6)' }}>
                  {spiceLevel && (
                    <div style={{ background: 'var(--brand-paper)', borderRadius: 'var(--radius-10)', padding: 'var(--space-10)', textAlign: 'center', border: '1px solid var(--neutral-100)' }}>
                      <p style={{ fontSize: 'var(--font-size-tiny)', color: 'var(--neutral-400)', margin: '0 0 4px', textTransform: 'uppercase', letterSpacing: 0.5 }}>Spice</p>
                      <SpiceIndicator level={spiceNum} />
                    </div>
                  )}
                  {book.pageCount && (
                    <div style={{ background: 'var(--brand-paper)', borderRadius: 'var(--radius-10)', padding: 'var(--space-10)', textAlign: 'center', border: '1px solid var(--neutral-100)' }}>
                      <p style={{ fontSize: 'var(--font-size-tiny)', color: 'var(--neutral-400)', margin: '0 0 4px', textTransform: 'uppercase', letterSpacing: 0.5 }}>Pages</p>
                      <p style={{ fontSize: 'var(--font-size-md)', margin: 'var(--space-none)', fontWeight: 700, color: 'var(--neutral-800)' }}>{book.pageCount}</p>
                    </div>
                  )}
                  {book.avgRating && (
                    <div style={{ background: 'var(--brand-paper)', borderRadius: 'var(--radius-10)', padding: 'var(--space-10)', textAlign: 'center', border: '1px solid var(--neutral-100)' }}>
                      <p style={{ fontSize: 'var(--font-size-tiny)', color: 'var(--neutral-400)', margin: '0 0 4px', textTransform: 'uppercase', letterSpacing: 0.5 }}>Rating</p>
                      <p style={{ fontSize: 'var(--font-size-md)', margin: 'var(--space-none)', fontWeight: 700, color: 'var(--neutral-800)' }}>⭐ {Number(book.avgRating).toFixed(1)}</p>
                    </div>
                  )}
                  {genre && (
                    <div style={{ background: 'var(--brand-paper)', borderRadius: 'var(--radius-10)', padding: 'var(--space-10)', textAlign: 'center', border: '1px solid var(--neutral-100)' }}>
                      <p style={{ fontSize: 'var(--font-size-tiny)', color: 'var(--neutral-400)', margin: '0 0 4px', textTransform: 'uppercase', letterSpacing: 0.5 }}>Genre</p>
                      <p style={{ fontSize: 'var(--font-size-xs)', margin: 'var(--space-none)', fontWeight: 600, color: 'var(--neutral-800)', lineHeight: 1.3, wordBreak: 'break-word' }}>{genre}</p>
                    </div>
                  )}
                  {(goodreadsData?.avgRating || goodreadsChecking) && (
                    <div style={{ background: 'var(--brand-paper)', borderRadius: 'var(--radius-10)', padding: 'var(--space-10)', textAlign: 'center', border: '1px solid var(--neutral-100)' }}>
                      <p style={{ fontSize: 'var(--font-size-tiny)', color: 'var(--neutral-400)', margin: '0 0 4px', textTransform: 'uppercase', letterSpacing: 0.5 }}>Goodreads</p>
                      <p style={{ fontSize: 'var(--font-size-md)', margin: 'var(--space-none)', fontWeight: 700, color: 'var(--neutral-800)' }}>
                        {goodreadsChecking ? '…' : `★ ${Number(goodreadsData.avgRating).toFixed(2)}`}
                      </p>
                      {goodreadsData?.ratingsCount && (
                        <p style={{ fontSize: 'var(--font-size-tiny)', color: 'var(--neutral-400)', margin: '2px 0 0' }}>{goodreadsData.ratingsCount.toLocaleString()} ratings</p>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* RIGHT */}
              <div>
                <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 'var(--font-size-h4)', fontWeight: 700, color: 'var(--neutral-800)', margin: '0 0 4px', lineHeight: 1.2 }}>{book.title}</h2>
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)', margin: '0 0 16px' }}>
                  <p style={{ fontSize: 15, color: 'var(--neutral-500)', margin: 'var(--space-none)' }}>by {book.author}</p>
                  {book.authorVerified && (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--space-xs)', fontSize: 'var(--font-size-xxs)', fontWeight: 700, color: '#166534', background: '#dcfce7', border: '1px solid #bbf7d0', borderRadius: 'var(--radius-md)', padding: '2px 8px' }}>✓ Author Verified</span>
                  )}
                </div>
                {book.tropes.length > 0 && (
                  <div style={{ marginBottom: 'var(--space-lg)' }}>
                    <p style={{ fontSize: 'var(--font-size-xs)', fontWeight: 700, color: 'var(--neutral-800)', margin: '0 0 8px', letterSpacing: 0.5, textTransform: 'uppercase' }}>Tropes</p>
                    <div style={{ display: 'flex', gap: 'var(--space-6)', flexWrap: 'wrap' }}>
                      {book.tropes.map((t, i) => <span key={i} style={{ fontSize: 'var(--font-size-base)', padding: '6px 14px', borderRadius: 'var(--radius-xxl)', background: 'var(--feedback-warning-bg)', color: 'var(--feedback-warning-ink)', fontWeight: 500, border: '1px solid var(--feedback-warning-border)' }}>{t}</span>)}
                    </div>
                  </div>
                )}
                {(book.moods.length > 0 || book.pov.length > 0) && (
                  <div style={{ display: 'flex', gap: 'var(--space-lg)', marginBottom: 'var(--space-lg)', flexWrap: 'wrap' }}>
                    {book.moods.length > 0 && (
                      <div>
                        <p style={{ fontSize: 'var(--font-size-xs)', fontWeight: 700, color: 'var(--neutral-800)', margin: '0 0 6px', letterSpacing: 0.5, textTransform: 'uppercase' }}>Mood</p>
                        <div style={{ display: 'flex', gap: 'var(--space-xs)', flexWrap: 'wrap' }}>
                          {book.moods.map((m, i) => <span key={i} style={{ fontSize: 'var(--font-size-sm)', padding: '4px 12px', borderRadius: 14, background: 'var(--neutral-800)', color: 'var(--brand-paper)', fontWeight: 500 }}>{m}</span>)}
                        </div>
                      </div>
                    )}
                    {book.pov.length > 0 && (
                      <div>
                        <p style={{ fontSize: 'var(--font-size-xs)', fontWeight: 700, color: 'var(--neutral-800)', margin: '0 0 6px', letterSpacing: 0.5, textTransform: 'uppercase' }}>POV</p>
                        <div style={{ display: 'flex', gap: 'var(--space-xs)', flexWrap: 'wrap' }}>
                          {book.pov.map((p, i) => <span key={i} style={{ fontSize: 'var(--font-size-sm)', padding: '4px 12px', borderRadius: 14, background: 'var(--brand-cream)', color: 'var(--neutral-500)', fontWeight: 500 }}>{p}</span>)}
                        </div>
                      </div>
                    )}
                  </div>
                )}
                {book.settings.length > 0 && (
                  <div style={{ marginBottom: 'var(--space-lg)' }}>
                    <p style={{ fontSize: 'var(--font-size-xs)', fontWeight: 700, color: 'var(--neutral-800)', margin: '0 0 6px', letterSpacing: 0.5, textTransform: 'uppercase' }}>Setting</p>
                    <div style={{ display: 'flex', gap: 'var(--space-xs)', flexWrap: 'wrap' }}>
                      {book.settings.map((s, i) => <span key={i} style={{ fontSize: 'var(--font-size-sm)', padding: '4px 12px', borderRadius: 14, background: 'var(--feedback-success-bg)', color: '#166534', fontWeight: 500 }}>{s}</span>)}
                    </div>
                  </div>
                )}
                {book.description && (
                  <div style={{ marginBottom: 'var(--space-14)' }}>
                    <p style={{ fontSize: 'var(--font-size-xs)', fontWeight: 700, color: 'var(--neutral-800)', margin: '0 0 6px', letterSpacing: 0.5, textTransform: 'uppercase' }}>Synopsis</p>
                    <p style={{ fontSize: 'var(--font-size-md)', color: 'var(--neutral-600)', lineHeight: 1.7, margin: 'var(--space-none)' }}>
                      {expanded ? book.description : book.description.slice(0, 180) + (book.description.length > 180 ? '...' : '')}
                    </p>
                    {book.description.length > 180 && (
                      <button onClick={() => setExpanded(!expanded)} style={{ background: 'none', border: 'none', color: 'var(--feedback-warning)', fontSize: 'var(--font-size-sm)', fontWeight: 600, cursor: 'pointer', padding: '4px 0', marginTop: 'var(--space-xs)' }}>
                        {expanded ? 'Show less' : 'Read more'}
                      </button>
                    )}
                  </div>
                )}
                {book.representation.length > 0 && (
                  <div style={{ marginBottom: 'var(--space-14)' }}>
                    <p style={{ fontSize: 'var(--font-size-xs)', fontWeight: 700, color: 'var(--neutral-800)', margin: '0 0 6px', letterSpacing: 0.5, textTransform: 'uppercase' }}>Representation</p>
                    <div style={{ display: 'flex', gap: 'var(--space-xs)', flexWrap: 'wrap' }}>
                      {book.representation.map((r, i) => <span key={i} style={{ fontSize: 'var(--font-size-xs)', padding: '3px 10px', borderRadius: 'var(--radius-lg)', background: '#f5f3ff', color: '#7c3aed', fontWeight: 500 }}>{r}</span>)}
                    </div>
                  </div>
                )}
                {book.triggers.length > 0 && (
                  <div style={{ marginBottom: 'var(--space-14)' }}>
                    <p style={{ fontSize: 'var(--font-size-xxs)', color: 'var(--neutral-400)', margin: '0 0 4px' }}>⚠️ Content notes</p>
                    <div style={{ display: 'flex', gap: 'var(--space-xs)', flexWrap: 'wrap' }}>
                      {book.triggers.map((t, i) => <span key={i} style={{ fontSize: 'var(--font-size-xxs)', padding: '3px 8px', borderRadius: 6, background: 'var(--neutral-100)', color: 'var(--neutral-500)' }}>{t}</span>)}
                    </div>
                  </div>
                )}

                <GoodreadsExtrasSection goodreadsId={resolvedGoodreadsId} isMobile={false} />
              </div>
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: '40px 0' }}><p style={{ color: 'var(--neutral-500)' }}>Book not found</p></div>
          )}
        </div>

        {/* Sticky footer */}
        {!loading && book && (
          <div style={{ padding: '14px 24px', borderTop: '1px solid var(--neutral-100)', display: 'flex', gap: 'var(--space-10)', flexShrink: 0, background: 'var(--brand-white)', flexWrap: 'wrap' }}>
            {isInLibrary ? (
              <div style={{ flex: 2, display: 'flex', flexDirection: 'column', gap: 'var(--space-xs)' }}>
                <div style={{ padding: 'var(--space-14)', background: 'var(--brand-forest)', color: 'var(--brand-white)', borderRadius: 'var(--radius-lg)', fontSize: 'var(--font-size-md)', fontWeight: 700, textAlign: 'center' }}>
                  {currentShelf ? `✓ ${SHELF_LABELS[currentShelf] || 'In Your Library'}` : '✓ Already in Your Library'}
                </div>
                <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--neutral-500)', textAlign: 'center', margin: 'var(--space-none)' }}>This book is already saved in your library.</p>
              </div>
            ) : (
              <>
                <button onClick={() => handleAddToLibrary('tbr')} disabled={adding}
                  style={{ flex: 2, padding: 'var(--space-14)', background: adding ? 'var(--neutral-600)' : 'var(--neutral-800)', color: 'var(--brand-white)', border: 'none', borderRadius: 'var(--radius-lg)', fontSize: 'var(--font-size-md)', fontWeight: 700, cursor: adding ? 'not-allowed' : 'pointer' }}>
                  {adding ? 'Adding...' : '📚 Add to My Library'}
                </button>
                <button onClick={() => handleAddToLibrary('read')} disabled={adding}
                  style={{ flex: 1, padding: 'var(--space-14)', background: 'var(--neutral-100)', color: 'var(--neutral-800)', border: '1px solid var(--brand-sand)', borderRadius: 'var(--radius-lg)', fontSize: 'var(--font-size-base)', fontWeight: 600, cursor: adding ? 'not-allowed' : 'pointer', whiteSpace: 'nowrap' }}
                  onMouseEnter={e => { e.currentTarget.style.background = '#292524'; e.currentTarget.style.color = '#fff'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = '#f5f5f4'; e.currentTarget.style.color = '#292524'; }}>
                  ✅ Already Read
                </button>
              </>
            )}
            {purchaseLinks.slice(0, 2).map((link, i) => (
                  <a key={i} href={link.url} target="_blank" rel="noopener noreferrer"
                    style={{ flex: 1, padding: 'var(--space-14)', background: 'var(--feedback-warning-bg)', color: 'var(--feedback-warning-ink)', border: '1px solid var(--feedback-warning-border)', borderRadius: 'var(--radius-lg)', fontSize: 'var(--font-size-base)', fontWeight: 600, textAlign: 'center', textDecoration: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {link.retailer} ↗
                  </a>
                ))}
          </div>
        )}
      </div>

      <style>{`
        @keyframes modalSlideUp { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
      `}</style>
    </div>
  );
}